"use client";

import { useEffect, useState, useTransition } from "react";
import { toggleSavedPlace } from "@/app/actions/saved-places";

/**
 * Shared save/wishlist toggle, used on the place detail toolbar (pill) and
 * on card tiles (icon). Optimistic on click, reconciled once the server
 * action resolves — the useTransition + disabled-while-pending guard
 * mirrors ImageUploadButton.tsx's fix for the upload/save race condition.
 */
export function SaveButton({
  slug,
  initialSaved,
  variant = "pill",
  className = "",
}: {
  slug: string;
  initialSaved: boolean;
  variant?: "pill" | "icon";
  className?: string;
}) {
  const [saved, setSaved] = useState(initialSaved);
  const [pending, startTransition] = useTransition();

  useEffect(() => setSaved(initialSaved), [initialSaved]);

  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Card tiles render this as a sibling of a full-bleed <Link>, not nested
    // inside it — stop the click from also triggering the card's own link.
    e.preventDefault();
    e.stopPropagation();

    const next = !saved;
    setSaved(next);
    startTransition(async () => {
      const result = await toggleSavedPlace(slug);
      setSaved(result.ok ? result.saved : !next);
    });
  };

  const label = saved ? "Saved" : "Save";
  const bookmark = (
    <svg
      width={variant === "icon" ? 16 : 15}
      height={variant === "icon" ? 16 : 15}
      viewBox="0 0 24 24"
      fill={saved ? "currentColor" : "none"}
      aria-hidden
    >
      <path d="M6 4h12v17l-6-4-6 4V4z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );

  if (variant === "icon") {
    // Every caller that overlays this on a card image passes `className="absolute
    // right-3 top-3"`. Tailwind emits its position utilities in a fixed order
    // (static, fixed, absolute, relative, sticky), so when both `relative` and
    // `absolute` land on the same element, `.relative` wins in the generated
    // CSS regardless of which one appears later in the class string — the
    // button silently fell back into normal document flow instead of floating
    // over the image. `relative` is only needed to anchor the `after:` tap-target
    // pseudo-element when the button ISN'T already positioned by its caller; an
    // absolutely/fixed/sticky-positioned element is already a valid anchor for
    // its own `::after`, so skip `relative` whenever the caller supplies one.
    const isExternallyPositioned = /\b(absolute|fixed|sticky)\b/.test(className);
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={pending}
        aria-pressed={saved}
        aria-label={saved ? "Remove from saved places" : "Save this place"}
        // after: grows the tappable area to 44px (WCAG target size) without
        // enlarging the visible 32px icon circle - same technique as
        // SocialLinks.tsx's icon variant.
        className={`${isExternallyPositioned ? "" : "relative"} inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors disabled:opacity-60 after:absolute after:-inset-1.5 after:content-[''] ${className}`}
        style={{
          borderColor: saved ? "var(--cta)" : "rgba(255,255,255,0.5)",
          background: saved ? "color-mix(in srgb, var(--cta) 85%, transparent)" : "rgba(3,12,9,0.45)",
          color: saved ? "#2b2b2b" : "#fff",
        }}
      >
        {bookmark}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      aria-pressed={saved}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold no-underline cursor-pointer transition-colors duration-150 font-[inherit] disabled:opacity-60 ${className}`}
      style={{
        borderColor: saved ? "var(--accent)" : "var(--border-subtle)",
        background: saved ? "color-mix(in srgb, var(--accent) 14%, transparent)" : "transparent",
        color: saved ? "var(--accent)" : "var(--foreground)",
      }}
    >
      {bookmark}
      {label}
    </button>
  );
}
